import { $session } from 'state/stores';

import { routesSettings } from 'constants/routes';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useMutation } from '@tanstack/react-query';
import { googleLogin, login } from 'api/services/auth';

import { LoginProps } from './Login.props';

import { $user } from 'state/stores/user';
import { GoogleSignupPayload } from 'types/auth';
import { UserDetail } from 'types/user';
import ToastHelper from 'utils/toast';
import LoginView from './Login.view';

const LoginContainer = (props: LoginProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { redirectUrl } = props;
  const returnUrlFromQuery = searchParams.get('returnUrl');
  const finalRedirectUrl = returnUrlFromQuery || redirectUrl;

  const goToRegister = () => {
    const signupUrl = routesSettings.AUTH_SIGNUP.path;
    const urlWithReturn = returnUrlFromQuery
      ? `${signupUrl}?returnUrl=${encodeURIComponent(returnUrlFromQuery)}`
      : signupUrl;

    navigate(urlWithReturn, {
      replace: true,
    });
  };

  const goToForgotPassword = () => {
    navigate(routesSettings.AUTH_FORGOT_PASSWORD.path, {
      replace: true,
    });
  };

  const { mutate, isPending } = useMutation({
    mutationFn: login,
    onSuccess: (data) => {
      const id = data.id;
      const tokens = data.tokens;
      const username = data.username;
      $session.actions.setSession({
        id,
        tokens,
        username,
      });
      $user.actions.setUser(data as unknown as UserDetail);

      if (finalRedirectUrl === 'MODAL_CONTEXT') {
        return;
      }

      navigate(finalRedirectUrl ?? routesSettings.HOME.path, {
        replace: true,
      });
    },
  });

  const { mutate: onGoogleLogin, isPending: googleLoginPending } = useMutation({
    mutationFn: (payload: GoogleSignupPayload) => googleLogin(payload),
    onSuccess: (data) => {
      const id = data.id;
      const tokens = data.tokens;
      const username = data.username;
      $session.actions.setSession({
        id,
        tokens,
        username,
        isAuth: true,
      });
      $user.actions.setUser(data as unknown as UserDetail);

      // If being used in modal context, don't navigate - let the modal context handle it
      if (finalRedirectUrl === 'MODAL_CONTEXT') {
        // The modal context will detect the authentication change and handle closing
        return;
      }

      navigate(finalRedirectUrl ?? routesSettings.HOME.path, {
        replace: true,
      });
    },
    onError: (error: any) => {
      if (error?.response?.status === 400) {
        ToastHelper.error('Invalid credentials, please signup');
      }
    },
  });

  return (
    <LoginView
      {...props}
      goToRegister={goToRegister}
      goToForgotPassword={goToForgotPassword}
      isLoading={isPending}
      isGoogleLoading={googleLoginPending}
      onSubmit={mutate}
      onGoogleLogin={onGoogleLogin}
    />
  );
};

export default LoginContainer;
